// src/lib/translation/route-announcer.ts
// Chrome's page translator rewrites the text Next.js portals into its route
// announcer, and React then fails to remove the rewritten node. Marking the
// host `translate="no"` before it holds text keeps the translator out.

const HOST_TAG = "next-route-announcer";

/** An `Element`-shaped value: only what this module calls. */
export interface AnnouncerHostLike {
  setAttribute(name: string, value: string): void;
  classList: { add(token: string): void };
}

export interface HostRootLike {
  querySelectorAll(selector: string): Iterable<AnnouncerHostLike>;
}

export interface ObserverLike {
  observe(target: object, options: { childList: boolean }): void;
  disconnect(): void;
}

export interface DocumentLike {
  body: HostRootLike | null;
  addEventListener(type: "DOMContentLoaded", listener: () => void): void;
}

/** Marks every route announcer host under `root`; returns how many. Idempotent. */
export function markRouteAnnouncer(root: HostRootLike): number {
  let count = 0;
  for (const host of root.querySelectorAll(HOST_TAG)) {
    host.setAttribute("translate", "no");
    host.classList.add("notranslate");
    count += 1;
  }
  return count;
}

/**
 * Marks the host now and whenever Next.js (re)creates it as a direct child of
 * `body`. Returns a function that stops watching.
 */
export function watchRouteAnnouncer(
  doc: DocumentLike,
  Observer: new (callback: () => void) => ObserverLike = MutationObserver,
): () => void {
  let observer: ObserverLike | null = null;
  let stopped = false;

  const start = (body: HostRootLike) => {
    if (stopped) return;
    markRouteAnnouncer(body);
    observer = new Observer(() => markRouteAnnouncer(body));
    observer.observe(body, { childList: true });
  };

  if (doc.body) start(doc.body);
  else
    doc.addEventListener("DOMContentLoaded", () => {
      if (doc.body) start(doc.body);
    });

  return () => {
    stopped = true;
    observer?.disconnect();
  };
}
